import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { getSafetyOverview, getUpcomingSafetyEvents } from "./safetyOverviewApi";

const DT_SAFETY_FONT_SCOPE = {
  "&, & *": {
    fontFamily:
      '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important',
    letterSpacing: "0 !important",
  },
  "& .MuiTypography-root": {
    fontWeight: "400 !important",
  },
  "& .MuiButton-root, & .MuiChip-label": {
    fontWeight: "700 !important",
    textTransform: "none !important",
  },
  "& .dt-safety-important": {
    fontWeight: "800 !important",
  },
};

const DARK_PANEL = {
  ...DT_SAFETY_FONT_SCOPE,
  border: "1px solid rgba(148, 163, 184, 0.3)",
  bgcolor: "rgba(4, 12, 28, 0.92)",
  color: "#f8fafc",
  boxShadow: "0 16px 34px rgba(0, 0, 0, 0.32)",
  backdropFilter: "blur(12px)",
};

const METRICS = [
  {
    key: "pending",
    label: "미평가",
    color: "#60a5fa",
    icon: AssignmentLateOutlinedIcon,
  },
  {
    key: "evaluated",
    label: "평가완료",
    color: "#22c55e",
    icon: FactCheckOutlinedIcon,
  },
  {
    key: "improving",
    label: "개선대기",
    color: "#f59e0b",
    icon: BuildCircleOutlinedIcon,
  },
  {
    key: "improved",
    label: "개선완료",
    color: "#34d399",
    icon: TaskAltOutlinedIcon,
  },
  {
    key: "highRisk",
    label: "고위험",
    color: "#ef4444",
    icon: WarningAmberRoundedIcon,
  },
];

const EVENT_META = {
  education: {
    label: "안전보건교육",
    shortLabel: "교육",
    color: "#60a5fa",
    bg: "rgba(37, 99, 235, 0.2)",
    icon: SchoolOutlinedIcon,
  },
  tbm: {
    label: "TBM 미실시",
    shortLabel: "TBM",
    color: "#fb7185",
    bg: "rgba(225, 29, 72, 0.2)",
    icon: GroupsOutlinedIcon,
  },
  inspection: {
    label: "안전점검 조치",
    shortLabel: "점검 조치",
    color: "#fb923c",
    bg: "rgba(234, 88, 12, 0.2)",
    icon: EngineeringOutlinedIcon,
  },
  improvement: {
    label: "개선 예정일",
    shortLabel: "개선",
    color: "#facc15",
    bg: "rgba(250, 204, 21, 0.18)",
    icon: BuildCircleOutlinedIcon,
  },
  msds: {
    label: "MSDS 만료",
    shortLabel: "MSDS",
    color: "#a78bfa",
    bg: "rgba(124, 58, 237, 0.2)",
    icon: ScienceOutlinedIcon,
  },
  evaluation: {
    label: "위험성평가 예정",
    shortLabel: "평가 예정",
    color: "#fb923c",
    bg: "rgba(234, 88, 12, 0.2)",
    icon: FactCheckOutlinedIcon,
  },
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatDate = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const startOfDay = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const getEventDateKey = (event) => String(event?.start || "").slice(0, 10);

const diffDaysFromToday = (dateKey, today) => {
  if (!dateKey) return 0;
  const eventDate = startOfDay(dateKey);
  const todayDate = startOfDay(today);
  return Math.round((eventDate.getTime() - todayDate.getTime()) / 86400000);
};

const getTimingLabel = (event, today) => {
  const diff = diffDaysFromToday(getEventDateKey(event), today);
  if (diff < 0) return `D+${Math.abs(diff)}`;
  if (diff === 0) return "오늘";
  return `D-${diff}`;
};

const formatKoreanDateTime = (value, allDay = false) => {
  if (!value) return "일정 미정";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const dateText = `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}.${String(date.getDate()).padStart(2, "0")}`;
  if (allDay || !String(value).includes("T")) return dateText;
  return `${dateText} ${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
};

const uniqueCount = (rows, key) =>
  new Set(
    (Array.isArray(rows) ? rows : []).map((row, index) =>
      row?.[key] == null ? `row-${index}` : String(row[key])
    )
  ).size;

const isCompletedImprovement = (row) => toNumber(row?.improve_status) >= 20;

const getBeforeRisk = (row) => {
  if (row?.risk_score != null) return toNumber(row.risk_score);
  return toNumber(row?.frequency_level) * toNumber(row?.importancy_level);
};

const isHighRisk = (row) => {
  return getEffectiveRiskScore(row) >= 16;
};

const getEffectiveRiskScore = (row) => {
  const afterRisk = row?.improve_after_risk == null ? null : toNumber(row.improve_after_risk);
  if (isCompletedImprovement(row) && afterRisk !== null) return afterRisk;
  return getBeforeRisk(row);
};

const getMachineName = (row) =>
  row?.safe_machine_name ||
  row?.machine_name ||
  row?.machine_manage_code ||
  (row?.machine_id ? `설비 ${row.machine_id}` : "미지정 설비");

const buildLatestEvaluations = (rows = []) => {
  const latestByCause = new Map();
  rows.forEach((row, index) => {
    const key = row?.register_cause_id == null ? `row-${index}` : String(row.register_cause_id);
    const previous = latestByCause.get(key);
    if (!previous || toNumber(row.eval_id) > toNumber(previous.eval_id)) latestByCause.set(key, row);
  });
  return Array.from(latestByCause.values());
};

const buildHighRiskMachines = (rows = []) => {
  const grouped = new Map();
  buildLatestEvaluations(rows)
    .map((row) => ({ ...row, effectiveRisk: getEffectiveRiskScore(row) }))
    .filter((row) => row.effectiveRisk >= 16)
    .forEach((row) => {
      const key = row?.machine_id == null ? getMachineName(row) : String(row.machine_id);
      const current = grouped.get(key) || {
        key,
        machineId: row?.machine_id,
        name: getMachineName(row),
        highestRisk: 0,
        highRiskCount: 0,
        improvingCount: 0,
        improvedHighRiskCount: 0,
        latestDate: "",
      };
      current.highestRisk = Math.max(current.highestRisk, row.effectiveRisk);
      current.highRiskCount += 1;
      if (toNumber(row?.improve_status) > 0 && !isCompletedImprovement(row)) {
        current.improvingCount += 1;
      }
      if (isCompletedImprovement(row)) current.improvedHighRiskCount += 1;
      const rowDate = String(row?.approve_date || row?.eval_date || "").slice(0, 10);
      if (rowDate > current.latestDate) current.latestDate = rowDate;
      grouped.set(key, current);
    });

  return Array.from(grouped.values())
    .sort((a, b) => {
      if (b.highestRisk !== a.highestRisk) return b.highestRisk - a.highestRisk;
      if (b.highRiskCount !== a.highRiskCount) return b.highRiskCount - a.highRiskCount;
      return String(a.name).localeCompare(String(b.name));
    })
    .slice(0, 5);
};

const getEventDiff = (event, today) => diffDaysFromToday(getEventDateKey(event), today);

const createSignalSummary = (events = [], today = new Date(), calendarData = {}) => {
  const todayKey = formatDate(today);
  const allEvents = Array.isArray(events) ? events : [];
  const tbmDay = (calendarData?.lists?.tbm || []).find((row) => row.date === todayKey);
  const inspectionAndImprove = allEvents.filter((event) =>
    ["inspection", "improvement"].includes(getEventSource(event))
  );
  const deadlineEvents = inspectionAndImprove.filter((event) => {
    const diff = getEventDiff(event, today);
    const status = getEventStatus(event);
    return diff <= 3 && !["done", "completed"].includes(status);
  });
  const msdsEvents = allEvents.filter((event) => getEventSource(event) === "msds");
  const msdsSignalEvents = msdsEvents.filter((event) => getEventDiff(event, today) <= 30);

  return {
    todayTbm: {
      completed: toNumber(tbmDay?.completed),
      missed: toNumber(tbmDay?.missed),
      unregistered: toNumber(tbmDay?.unregistered),
      total: toNumber(tbmDay?.total),
      sessions: tbmDay?.sessions || [],
    },
    deadline: {
      overdue: deadlineEvents.filter((event) => getEventDiff(event, today) < 0).length,
      dueSoon: deadlineEvents.filter((event) => {
        const diff = getEventDiff(event, today);
        return diff >= 0 && diff <= 3;
      }).length,
      events: [...deadlineEvents].sort((a, b) => getEventDiff(a, today) - getEventDiff(b, today)),
    },
    msds: {
      expired: msdsEvents.filter((event) => getEventDiff(event, today) < 0).length,
      due: msdsSignalEvents.filter((event) => {
        const diff = getEventDiff(event, today);
        return diff >= 0 && diff <= 30;
      }).length,
      events: [...msdsSignalEvents].sort((a, b) => getEventDiff(a, today) - getEventDiff(b, today)),
    },
  };
};

const createTodayTbmDetailEvent = (todayTbm = {}, today = new Date()) => {
  const dateKey = formatDate(today);
  const sessions = [...(todayTbm.sessions || [])].sort((a, b) =>
    String(a.team_name || "").localeCompare(String(b.team_name || ""))
  );
  const childEvents = sessions.map((session) => ({
    id: `tbm-today-${session.id || session.department_id || session.team_name}`,
    title: session.team_name || "TBM",
    raw: session,
  }));

  return {
    id: `tbm-today-summary-${dateKey}`,
    source: "tbm",
    type: "tbm_today_summary",
    title: "TBM",
    start: dateKey,
    status: todayTbm.missed > 0 ? "missed" : todayTbm.unregistered > 0 ? "unregistered" : "completed",
    allDay: true,
    meta: EVENT_META.tbm,
    displayTitle: "오늘의 TBM 현황",
    timingLabel: getTimingLabel({ start: dateKey }, today),
    isTbmGroup: true,
    childEvents,
    raw: {
      date: dateKey,
      completed_count: todayTbm.completed || 0,
      missed_count: todayTbm.missed || 0,
      unregistered_count: todayTbm.unregistered || 0,
      sessions,
    },
  };
};

const createSignalListEvent = ({
  id,
  title,
  source,
  meta,
  events = [],
  today = new Date(),
}) => ({
  id,
  source,
  type: "signal_list",
  title,
  start: formatDate(today),
  allDay: true,
  meta,
  displayTitle: title,
  childEvents: events.map((event) => ({
    ...event,
    displayTitle: event.displayTitle || getEventTitle(event, getEventSource(event)),
    timingLabel: getTimingLabel(event, today),
  })),
});

const getTbmStatusChipSx = (status) => {
  if (status === "completed") return { label: "완료", bgcolor: "#dcfce7", color: "#16a34a" };
  if (status === "missed") return { label: "미실시", bgcolor: "#fee2e2", color: "#dc2626" };
  return { label: "미등록", bgcolor: "#ffedd5", color: "#ea580c" };
};

const getEventSource = (event) => {
  const source = String(event?.source || event?.type || "").toLowerCase();
  if (source.includes("education")) return "education";
  if (source.includes("tbm")) return "tbm";
  if (source.includes("inspection")) return "inspection";
  if (source.includes("improve")) return "improvement";
  if (source.includes("msds")) return "msds";
  if (source.includes("evaluation") || source.includes("risk")) return "evaluation";
  return "inspection";
};

const getEventActionPath = (event, groupCode, projectCode) => {
  const base = `/safe/${groupCode}/${projectCode}`;
  const source = getEventSource(event);
  if (source === "tbm") return `${base}/monitoring/tbm`;
  if (source === "education") return `${base}/monitoring/education`;
  if (source === "msds") return `${base}/standard/msds`;
  if (source === "improvement") return `${base}/assessment/wizard/step-3`;
  if (source === "evaluation") return `${base}/assessment/wizard/step-1`;
  if (source === "inspection") return `${base}/monitoring/actions`;
  return base;
};

const getEventTitle = (event, source) => {
  if (source === "tbm") return `${event?.title || "대상 팀"} TBM 미실시`;
  if (source === "improvement") return `${event?.title || "개선대책"} 예정`;
  if (source === "msds") return `${event?.title || "MSDS"} 만료 예정`;
  if (source === "evaluation") return event?.title || "위험성평가 예정";
  return event?.title || EVENT_META[source].label;
};

const getEventStatus = (event) =>
  String(
    event?.status ||
      event?.raw?.effective_sla_status ||
      event?.raw?.sla_status ||
      event?.raw?.status ||
      ""
  ).toLowerCase();

const createTbmGroupEvent = (dateKey, groupEvents, today) => {
  const sortedEvents = [...groupEvents].sort((a, b) =>
    String(a.raw?.team_name || a.title).localeCompare(String(b.raw?.team_name || b.title))
  );
  return {
    id: `tbm-group-${dateKey}`,
    source: "tbm",
    type: "tbm_group",
    title: "TBM 미실시",
    start: dateKey,
    status: "missed",
    allDay: true,
    meta: EVENT_META.tbm,
    displayTitle: `TBM 미실시 ${sortedEvents.length}팀`,
    timingLabel: getTimingLabel({ start: dateKey }, today),
    isTbmGroup: true,
    childEvents: sortedEvents,
    raw: {
      date: dateKey,
      missed_count: sortedEvents.length,
      sessions: sortedEvents.map((event) => event.raw || event),
    },
  };
};

const groupTbmMissedByDate = (events, today, options = {}) => {
  const todayKey = formatDate(today);
  const groups = new Map();
  const rest = [];

  events.forEach((event) => {
    const source = getEventSource(event);
    const status = getEventStatus(event);
    const dateKey = getEventDateKey(event);
    const shouldGroup =
      source === "tbm" &&
      status === "missed" &&
      dateKey &&
      (!options.onlyToday || dateKey === todayKey);

    if (!shouldGroup) {
      if (!(options.onlyToday && source === "tbm")) rest.push(event);
      return;
    }

    groups.set(dateKey, [...(groups.get(dateKey) || []), event]);
  });

  return [
    ...rest,
    ...Array.from(groups.entries()).map(([dateKey, groupEvents]) =>
      createTbmGroupEvent(dateKey, groupEvents, today)
    ),
  ].sort((a, b) => {
    const dateOrder = String(a.start).localeCompare(String(b.start));
    if (dateOrder !== 0) return dateOrder;
    return String(a.displayTitle).localeCompare(String(b.displayTitle));
  });
};

const normalizeUpcomingEvents = (events, today) => {
  const todayKey = formatDate(today);
  const normalized = (Array.isArray(events) ? events : [])
    .filter((event) => {
      const source = getEventSource(event);
      const dateKey = String(event?.start || "").slice(0, 10);
      if (!dateKey || dateKey < todayKey) return false;
      if (source === "tbm") return event.status === "missed";
      if (source === "education") return event.status !== "cancelled";
      if (source === "inspection") return !["done", "completed"].includes(event.status);
      return true;
    })
    .map((event) => {
      const source = getEventSource(event);
      return {
        ...event,
        source,
        meta: EVENT_META[source],
        displayTitle: getEventTitle(event, source),
        timingLabel: getTimingLabel(event, today),
      };
    });

  return groupTbmMissedByDate(normalized, today);
};

const normalizeCalendarEvents = (events) =>
  (Array.isArray(events) ? events : [])
    .filter((event) => {
      const source = getEventSource(event);
      if (!event?.start) return false;
      if (source === "tbm") return event.status === "missed";
      if (source === "education") return event.status !== "cancelled";
      if (source === "inspection") {
        return !["done", "completed"].includes(event.status);
      }
      return true;
    })
    .map((event) => {
      const source = getEventSource(event);
      return {
        ...event,
        source,
        meta: EVENT_META[source],
        displayTitle: getEventTitle(event, source),
      };
    })
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));

const isPriorityEvent = (event, today) => {
  const source = getEventSource(event);
  const status = getEventStatus(event);
  const diff = diffDaysFromToday(getEventDateKey(event), today);

  if (source === "inspection") return status === "overdue" || diff < 0;
  if (source === "tbm") return status === "missed" && diff === 0;
  if (source === "improvement") return diff <= 0;
  if (source === "msds") return diff <= 0;
  if (source === "education") {
    return diff < 0 && !["completed", "cancelled"].includes(status);
  }
  return diff < 0;
};

const normalizePriorityEvents = (events, today) =>
  groupTbmMissedByDate(
    normalizeCalendarEvents(events).filter((event) => isPriorityEvent(event, today)),
    today,
    { onlyToday: true }
  )
    .map((event) => ({
      ...event,
      timingLabel: getTimingLabel(event, today),
    }))
    .sort((a, b) => {
      const dateOrder = String(a.start).localeCompare(String(b.start));
      if (dateOrder !== 0) return dateOrder;
      return String(a.displayTitle).localeCompare(String(b.displayTitle));
    });

const getMonthGrid = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  return [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: lastDate }, (_, index) => index + 1),
  ];
};

const detailRows = (event) => {
  if (!event) return [];
  const raw = event.raw || {};
  if (event.source === "education") {
    return [
      ["교육 유형", raw.edu_type],
      ["교육 대상", raw.target_desc],
      ["교육 장소", raw.location_name],
      ["교육 시간", raw.duration_min ? `${raw.duration_min}분` : null],
    ];
  }
  if (event.source === "tbm") {
    return [
      ["대상 팀", raw.team_name || event.title],
      ["상태", "미실시"],
      ["전체 대상", raw.total_members ? `${raw.total_members}명` : null],
    ];
  }
  if (event.source === "improvement") {
    return [
      ["대상", [raw.machine_name, raw.process_name].filter(Boolean).join(" · ")],
      ["위험요인", raw.danger_description],
      ["개선대책", raw.improve_plan],
      ["담당자", raw.emp_name || raw.assignee_name],
      ["예정일", raw.expected_date],
      ["현재 위험성", raw.risk_score ? `${raw.risk_score}점` : null],
    ];
  }
  if (event.source === "inspection") {
    return [
      ["조치 상태", raw.effective_sla_status || raw.sla_status || event.status],
      ["담당자", raw.assignee_name || raw.assignee_emp_name],
      ["조치 내용", raw.instruction],
      ["점검 내용", raw.inspection_content || raw.content],
    ];
  }
  return [
    ["제품명", raw.product_name || event.title],
    ["제조사", raw.manufacturer],
    ["주요 물질", raw.substance_name],
    ["CAS No.", raw.cas_no],
  ];
};

function MonthlyStatusPanel({
  monthLabel,
  metrics,
  loading,
  hasError,
  highRiskMachines,
  collapsed,
  onToggle,
}) {
  const total = metrics.pending + metrics.evaluated;
  const completionRate = total ? Math.round((metrics.evaluated / total) * 100) : 0;

  if (collapsed) {
    return (
      <Paper
        elevation={0}
        role="button"
        tabIndex={0}
        aria-label="위험성평가 현황 펼치기"
        onClick={onToggle}
        onKeyDown={(keyEvent) => {
          if (keyEvent.key === "Enter" || keyEvent.key === " ") {
            keyEvent.preventDefault();
            onToggle();
          }
        }}
        sx={{
          ...DARK_PANEL,
          position: "absolute",
          top: 72,
          left: 102,
          zIndex: 24,
          width: 46,
          height: 46,
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          border: "1px solid rgba(217, 164, 82, 0.48)",
          transition: "transform 150ms ease, border-color 150ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            borderColor: "rgba(251, 191, 36, 0.78)",
          },
        }}
      >
        <FactCheckOutlinedIcon sx={{ color: "#ffffff", fontSize: 22 }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      data-testid="dt-monthly-risk-overview"
      sx={{
        ...DARK_PANEL,
        position: "absolute",
        top: 72,
        left: 102,
        width: 430,
        border: "1px solid rgba(217, 164, 82, 0.48)",
        bgcolor: "rgba(2, 8, 19, 0.91)",
        backgroundImage:
          "linear-gradient(135deg, rgba(15, 28, 48, 0.96), rgba(2, 8, 19, 0.92) 62%)",
        boxShadow: "0 18px 42px rgba(0, 0, 0, 0.42)",
        borderRadius: "6px",
        zIndex: 24,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.7, pt: 1.45, pb: 1.15 }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography
            className="dt-safety-important"
            sx={{
              fontSize: 14.5,
              fontWeight: 700,
              letterSpacing: 0,
              color: "#ffffff",
            }}
          >
            {monthLabel} 위험성평가 현황
          </Typography>
          <Tooltip title="현재 평가 주기 기준">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          {hasError && (
            <Chip
              label="일부 조회 지연"
              size="small"
              sx={{ height: 21, bgcolor: "rgba(245,158,11,.16)", color: "#fbbf24" }}
            />
          )}
          <Tooltip title="현황 접기">
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label="위험성평가 현황 접기"
              sx={{
                width: 25,
                height: 25,
                color: "#ffffff",
                bgcolor: "rgba(255,255,255,.08)",
                "&:hover": { bgcolor: "rgba(255,255,255,.16)" },
              }}
            >
              <KeyboardArrowUpRoundedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          px: 0.95,
          pb: 1.2,
        }}
      >
        {METRICS.map((metric, index) => {
          return (
            <Box
              key={metric.key}
              sx={{
                minWidth: 0,
                px: 0.65,
                textAlign: "center",
                borderLeft: index ? "1px solid rgba(148,163,184,.28)" : 0,
              }}
            >
              <Typography
                noWrap
                sx={{ color: "#cbd5e1", fontSize: 10.5, fontWeight: 500 }}
              >
                {metric.label}
              </Typography>
              {loading ? (
                <Skeleton
                  width={28}
                  height={31}
                  sx={{ mx: "auto", bgcolor: "rgba(255,255,255,.1)" }}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 0.45,
                    color: metric.color,
                    fontSize: 25,
                    lineHeight: 1.1,
                    fontWeight: 800,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {metrics[metric.key]}
                  <Box component="span" sx={{ ml: 0.25, color: "#94a3b8", fontSize: 10 }}>
                    건
                  </Box>
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.1}
        sx={{ px: 1.7, pb: 1.45 }}
      >
        <Typography sx={{ color: "#cbd5e1", fontSize: 10.5, fontWeight: 500, flexShrink: 0 }}>
          평가 완료율 {completionRate}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={completionRate}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 4,
            bgcolor: "rgba(148,163,184,.18)",
            "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#22c55e" },
          }}
        />
      </Stack>

      <Box sx={{ px: 1.2, pb: 1.25 }}>
        <Box
          sx={{
            borderTop: "1px solid rgba(148, 163, 184, 0.18)",
            pt: 1,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Stack direction="row" alignItems="center" spacing={0.65}>
              <EngineeringOutlinedIcon sx={{ color: "#f87171", fontSize: 15 }} />
              <Typography sx={{ color: "#f8fafc", fontSize: 12, fontWeight: 700 }}>
                고위험 설비 Top 5
              </Typography>
            </Stack>
            <Typography sx={{ color: "#94a3b8", fontSize: 10.5 }}>
              현재 위험점수 기준
            </Typography>
          </Stack>

          {loading ? (
            <Stack spacing={0.5}>
              {[0, 1, 2].map((item) => (
                <Skeleton
                  key={item}
                  height={30}
                  sx={{ bgcolor: "rgba(255,255,255,.08)", borderRadius: "6px" }}
                />
              ))}
            </Stack>
          ) : highRiskMachines?.length ? (
            <Stack spacing={0.45}>
              {highRiskMachines.map((machine, index) => (
                <Box
                  key={machine.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "20px minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 0.75,
                    px: 0.8,
                    py: 0.65,
                    borderRadius: "6px",
                    bgcolor: "rgba(15, 23, 42, 0.78)",
                    border: "1px solid rgba(148, 163, 184, 0.16)",
                  }}
                >
                  <Typography
                    sx={{
                      color: index === 0 ? "#fecaca" : "#cbd5e1",
                      fontSize: 12,
                      fontWeight: 800,
                      textAlign: "center",
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ color: "#f8fafc", fontSize: 12.2, fontWeight: 650 }}>
                      {machine.name}
                    </Typography>
                    <Typography noWrap sx={{ mt: 0.1, color: "#94a3b8", fontSize: 10.5 }}>
                      고위험 {machine.highRiskCount}건 · 개선중 {machine.improvingCount}건
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`${machine.highestRisk}점`}
                    sx={{
                      height: 22,
                      borderRadius: "5px",
                      bgcolor: "rgba(239, 68, 68, 0.18)",
                      color: "#fecaca",
                      fontSize: 11,
                      fontWeight: 800,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            <Box
              sx={{
                border: "1px dashed rgba(148, 163, 184, 0.2)",
                borderRadius: "6px",
                px: 1,
                py: 0.75,
              }}
            >
              <Typography sx={{ color: "#94a3b8", fontSize: 11.5 }}>
                현재 고위험 설비가 없습니다.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function UpcomingToast({ event, onOpen, onClose, priority = false }) {
  const EventIcon = event.meta.icon;
  const accentColor = priority ? "#ef4444" : event.meta.color;
  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(event, { priority })}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === "Enter" || keyEvent.key === " ") {
          keyEvent.preventDefault();
          onOpen(event, { priority });
        }
      }}
      sx={{
        ...DARK_PANEL,
        width: "100%",
        borderRadius: "6px",
        borderLeft: `3px solid ${accentColor}`,
        cursor: "pointer",
        bgcolor: "rgba(3, 11, 25, 0.94)",
        transition:
          "transform 150ms ease, border-color 150ms ease, background-color 150ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          bgcolor: "rgba(10, 22, 43, 0.96)",
          borderColor: accentColor,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.1, py: 0.9 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "7px",
            bgcolor: event.meta.bg,
            color: event.meta.color,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <EventIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.6}>
            {event.timingLabel ? (
              <Chip
                size="small"
                label={event.timingLabel}
                sx={{
                  height: 18,
                  borderRadius: "4px",
                  color: priority ? "#fecaca" : "#dbeafe",
                  bgcolor: priority
                    ? "rgba(239, 68, 68, 0.18)"
                    : "rgba(96, 165, 250, 0.16)",
                  fontSize: 10,
                  fontWeight: 650,
                  "& .MuiChip-label": { px: 0.65 },
                }}
              />
            ) : null}
            <Typography sx={{ color: event.meta.color, fontSize: 10.5, fontWeight: 650 }}>
              {event.meta.shortLabel}
            </Typography>
            <Typography sx={{ color: "#94a3b8", fontSize: 10.5 }}>
              {formatKoreanDateTime(event.start, event.allDay)}
            </Typography>
          </Stack>
          <Typography
            noWrap
            sx={{ mt: 0.15, color: "#f8fafc", fontSize: 12.5, fontWeight: 600 }}
          >
            {event.displayTitle}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="일정 알림 닫기"
          onClick={(clickEvent) => {
            clickEvent.stopPropagation();
            onClose(event.id);
          }}
          sx={{ color: "#94a3b8", p: 0.25, "&:hover": { color: "#fff" } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <ChevronRightIcon sx={{ fontSize: 17, color: "#64748b" }} />
      </Stack>
    </Paper>
  );
}

function CompactCalendarButton({ onOpen }) {
  return (
    <Paper elevation={0} sx={{ bgcolor: "rgba(15, 23, 42, 0.72)", borderRadius: "6px", p: 1 }}>
      <Stack spacing={0.8}>
        <Typography sx={{ color: "#94a3b8", fontSize: 11.5 }}>
          다가오는 일정이 없습니다.
        </Typography>
        <Box
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={(keyEvent) => {
            if (keyEvent.key === "Enter" || keyEvent.key === " ") {
              keyEvent.preventDefault();
              onOpen();
            }
          }}
          sx={{
            border: "1px solid rgba(96, 165, 250, 0.34)",
            bgcolor: "rgba(37, 99, 235, 0.14)",
            color: "#ffffff",
            borderRadius: "6px",
            px: 1,
            py: 0.75,
            cursor: "pointer",
            transition: "transform 150ms ease, border-color 150ms ease, background-color 150ms ease",
            "&:hover": {
              transform: "translateY(-1px)",
              borderColor: "rgba(96, 165, 250, 0.66)",
              bgcolor: "rgba(37, 99, 235, 0.22)",
            },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.8}>
            <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: "#ffffff" }} />
            <Typography sx={{ color: "#ffffff", fontSize: 11.5, fontWeight: 650 }}>
              안전상황 한눈에 보기
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function SignalStatBox({ title, icon, children, accent = "#60a5fa", onClick }) {
  const Icon = icon;
  return (
    <Box
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(keyEvent) => {
        if (!onClick) return;
        if (keyEvent.key === "Enter" || keyEvent.key === " ") {
          keyEvent.preventDefault();
          onClick();
        }
      }}
      sx={{
        minWidth: 0,
        flex: 1,
        borderRadius: "6px",
        border: "1px solid rgba(148, 163, 184, 0.16)",
        bgcolor: "rgba(15, 23, 42, 0.7)",
        px: 0.85,
        py: 0.75,
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 150ms ease, background-color 150ms ease, transform 150ms ease",
        "&:hover": onClick
          ? {
              borderColor: `${accent}88`,
              bgcolor: "rgba(15, 23, 42, 0.9)",
              transform: "translateY(-1px)",
            }
          : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.8}>
        <Stack direction="row" alignItems="center" spacing={0.55} sx={{ minWidth: 0 }}>
          {Icon ? <Icon sx={{ color: accent, fontSize: 14, flexShrink: 0 }} /> : null}
          <Typography noWrap className="dt-safety-important" sx={{ color: "#cbd5e1", fontSize: 10.8, fontWeight: 650 }}>
            {title}
          </Typography>
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0, flexShrink: 0 }}>
          {children}
        </Box>
      </Stack>
    </Box>
  );
}

function CountChip({ label, value, color = "#60a5fa" }) {
  return (
    <Chip
      size="small"
      label={`${label} ${value}`}
      sx={{
        height: 20,
        borderRadius: "5px",
        color,
        bgcolor: `${color}22`,
        border: `1px solid ${color}55`,
        fontSize: 10.5,
        fontWeight: 700,
        "& .MuiChip-label": { px: 0.65 },
      }}
    />
  );
}

function ActionQueuePanel({
  priorityEvents,
  futureEvents,
  signalSummary,
  today,
  onOpen,
  onClose,
  onOpenCalendar,
  collapsed,
  onToggle,
}) {
  const visiblePriority = priorityEvents;
  const visibleFuture = futureEvents.slice(0, 3);
  const todayTbm = signalSummary?.todayTbm || {};
  const deadline = signalSummary?.deadline || {};
  const msds = signalSummary?.msds || {};

  if (collapsed) {
    return (
      <Paper
        elevation={0}
        role="button"
        tabIndex={0}
        aria-label="우선 처리 및 일정 펼치기"
        onClick={onToggle}
        onKeyDown={(keyEvent) => {
          if (keyEvent.key === "Enter" || keyEvent.key === " ") {
            keyEvent.preventDefault();
            onToggle();
          }
        }}
        sx={{
          ...DARK_PANEL,
          position: "absolute",
          left: 102,
          bottom: 22,
          zIndex: 26,
          width: 48,
          height: 48,
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          border: "1px solid rgba(96, 165, 250, 0.42)",
          transition: "transform 150ms ease, border-color 150ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            borderColor: "rgba(255, 255, 255, 0.72)",
          },
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ color: "#ffffff", fontSize: 24 }} />
      </Paper>
    );
  }

  return (
    <Paper
      data-testid="dt-upcoming-safety-events"
      elevation={0}
      sx={{
        ...DARK_PANEL,
        position: "absolute",
        left: 102,
        bottom: 22,
        zIndex: 26,
        width: 332,
        maxHeight: "min(430px, calc(100vh - 210px))",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Stack
        spacing={0.85}
        sx={{
          p: 1,
          maxHeight: "inherit",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(148, 163, 184, 0.45) transparent",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(148, 163, 184, 0.45)",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(203, 213, 225, 0.65)",
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.65}>
            <WarningAmberRoundedIcon sx={{ color: "#fb7185", fontSize: 16 }} />
            <Typography sx={{ color: "#f8fafc", fontSize: 12, fontWeight: 650 }}>
              우선 처리
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.45}>
            <Tooltip title="안전상황 한눈에 보기">
              <IconButton
                size="small"
                onClick={onOpenCalendar}
                aria-label="안전상황 한눈에 보기"
                sx={{ width: 24, height: 24, color: "#ffffff" }}
              >
                <CalendarMonthOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Chip
              size="small"
              label={`${priorityEvents.length}건`}
              sx={{
                height: 20,
                borderRadius: "5px",
                color: priorityEvents.length ? "#fecaca" : "#cbd5e1",
                bgcolor: priorityEvents.length
                  ? "rgba(239, 68, 68, 0.18)"
                  : "rgba(148, 163, 184, 0.12)",
                fontSize: 10.5,
                fontWeight: 650,
                "& .MuiChip-label": { px: 0.7 },
              }}
            />
            <Tooltip title="패널 접기">
              <IconButton
                size="small"
                onClick={onToggle}
                aria-label="우선 처리 및 일정 접기"
                sx={{ width: 24, height: 24, color: "#ffffff" }}
              >
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {visiblePriority.length > 0 ? (
          <Stack
            spacing={0.55}
            sx={{
              maxHeight: 188,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.25,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(148, 163, 184, 0.45) transparent",
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(148, 163, 184, 0.45)",
                borderRadius: 999,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "rgba(203, 213, 225, 0.65)",
              },
            }}
          >
            {visiblePriority.map((event) => (
              <UpcomingToast
                key={`priority-${event.id}`}
                event={event}
                priority
                onOpen={onOpen}
                onClose={onClose}
              />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              border: "1px dashed rgba(148, 163, 184, 0.24)",
              borderRadius: "6px",
              px: 1,
              py: 0.8,
              color: "#94a3b8",
              fontSize: 11.5,
            }}
          >
            지연된 항목이 없습니다.
          </Box>
        )}

        <Divider sx={{ borderColor: "rgba(148, 163, 184, 0.18)" }} />

        <SignalStatBox
          title="오늘의 TBM 현황"
          icon={GroupsOutlinedIcon}
          accent="#38bdf8"
          onClick={() => onOpen(createTodayTbmDetailEvent(todayTbm), { priority: true })}
        >
          <Stack direction="row" spacing={0.45} sx={{ flexWrap: "nowrap" }}>
            <CountChip label="완료" value={`${todayTbm.completed || 0}팀`} color="#34d399" />
            <CountChip label="미실시" value={`${todayTbm.missed || 0}팀`} color="#fb7185" />
          </Stack>
        </SignalStatBox>

        <SignalStatBox
          title="기한 관리"
          icon={AssignmentLateOutlinedIcon}
          accent="#fb923c"
          onClick={() =>
            onOpen(
              createSignalListEvent({
                id: "deadline-signal-list",
                title: "기한 관리",
                source: "inspection",
                meta: EVENT_META.inspection,
                events: deadline.events || [],
                today,
              }),
              { priority: true }
            )
          }
        >
          <Stack direction="row" spacing={0.45} sx={{ flexWrap: "nowrap" }}>
            <CountChip label="초과" value={`${deadline.overdue || 0}건`} color="#f87171" />
            <CountChip label="임박" value={`${deadline.dueSoon || 0}건`} color="#fbbf24" />
          </Stack>
        </SignalStatBox>

        <SignalStatBox
          title="MSDS 만료"
          icon={ScienceOutlinedIcon}
          accent="#a78bfa"
          onClick={() =>
            onOpen(
              createSignalListEvent({
                id: "msds-signal-list",
                title: "MSDS 만료",
                source: "msds",
                meta: EVENT_META.msds,
                events: msds.events || [],
                today,
              }),
              { priority: true }
            )
          }
        >
          <Stack direction="row" spacing={0.45} sx={{ flexWrap: "nowrap" }}>
            <CountChip label="만료" value={`${msds.expired || 0}건`} color="#f87171" />
            <CountChip label="30일 내" value={`${msds.due || 0}건`} color="#a78bfa" />
          </Stack>
        </SignalStatBox>

        <Divider sx={{ borderColor: "rgba(148, 163, 184, 0.18)" }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.65}>
            <CalendarMonthOutlinedIcon sx={{ color: "#93c5fd", fontSize: 15 }} />
            <Typography sx={{ color: "#e2e8f0", fontSize: 12, fontWeight: 650 }}>
              다가오는 일정
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.45}>
            <Tooltip title="안전상황 한눈에 보기">
              <IconButton
                size="small"
                onClick={onOpenCalendar}
                aria-label="안전상황 한눈에 보기"
                sx={{ width: 24, height: 24, color: "#ffffff" }}
              >
                <CalendarMonthOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Chip
              size="small"
              label={`${futureEvents.length}건`}
              sx={{
                height: 20,
                borderRadius: "5px",
                color: "#dbeafe",
                bgcolor: "rgba(96, 165, 250, 0.14)",
                fontSize: 10.5,
                fontWeight: 650,
                "& .MuiChip-label": { px: 0.7 },
              }}
            />
          </Stack>
        </Stack>

        {visibleFuture.length > 0 ? (
          <Stack spacing={0.55}>
            {visibleFuture.map((event) => (
              <UpcomingToast
                key={`future-${event.id}`}
                event={event}
                onOpen={onOpen}
                onClose={onClose}
              />
            ))}
          </Stack>
        ) : (
          <CompactCalendarButton onOpen={onOpenCalendar} />
        )}
      </Stack>
    </Paper>
  );
}

function SafetyCalendarDialog({
  open,
  monthDate,
  events,
  loading,
  onMonthChange,
  onEventOpen,
  onClose,
}) {
  const monthCells = useMemo(() => getMonthGrid(monthDate), [monthDate]);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const monthEvents = useMemo(
    () => {
      const filteredEvents = events.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate.getFullYear() === year && eventDate.getMonth() === month;
      });
      return groupTbmMissedByDate(filteredEvents, monthDate);
    },
    [events, month, monthDate, year]
  );
  const eventsByDay = useMemo(() => {
    const map = new Map();
    monthEvents.forEach((event) => {
      const day = new Date(event.start).getDate();
      map.set(day, [...(map.get(day) || []), event]);
    });
    return map;
  }, [monthEvents]);

  return (
    <Dialog
      data-testid="dt-safety-calendar-dialog"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          ...DT_SAFETY_FONT_SCOPE,
          height: "min(790px, calc(100vh - 56px))",
          borderRadius: "8px",
          color: "#f8fafc",
          bgcolor: "#06101f",
          backgroundImage:
            "linear-gradient(145deg, rgba(12, 27, 49, .98), rgba(3, 10, 22, .99))",
          border: "1px solid rgba(148, 163, 184, .28)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: 2.4, py: 1.65, borderBottom: "1px solid rgba(148,163,184,.2)" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.1}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "8px",
                display: "grid",
                placeItems: "center",
                color: "#93c5fd",
                bgcolor: "rgba(37, 99, 235, .2)",
              }}
            >
              <CalendarMonthOutlinedIcon />
            </Box>
            <Box>
              <Typography className="dt-safety-important" sx={{ color: "#ffffff", fontSize: 17, fontWeight: 700 }}>
                안전상황 캘린더
              </Typography>
              <Typography sx={{ mt: 0.1, fontSize: 11.5, color: "#94a3b8" }}>
                교육, TBM 미실시, 점검 조치, 개선 예정일과 MSDS 만료 일정을 확인합니다.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} aria-label="안전상황 캘린더 닫기" sx={{ color: "#cbd5e1" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box sx={{ px: 2.4, pt: 1.45, pb: 1.1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <IconButton
              aria-label="이전 달"
              onClick={() => onMonthChange(new Date(year, month - 1, 1))}
              sx={{ color: "#e2e8f0", border: "1px solid rgba(148,163,184,.25)" }}
            >
              <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              className="dt-safety-important"
              sx={{
                minWidth: 145,
                color: "#ffffff",
                textAlign: "center",
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              {year}년 {month + 1}월
            </Typography>
            <IconButton
              aria-label="다음 달"
              onClick={() => onMonthChange(new Date(year, month + 1, 1))}
              sx={{ color: "#e2e8f0", border: "1px solid rgba(148,163,184,.25)" }}
            >
              <ArrowForwardIosRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={0.65}>
            {Object.values(EVENT_META).map((meta) => (
              <Chip
                key={meta.shortLabel}
                label={meta.shortLabel}
                size="small"
                sx={{ height: 24, bgcolor: meta.bg, color: meta.color, fontWeight: 650 }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      {loading && (
        <LinearProgress
          sx={{
            height: 2,
            bgcolor: "rgba(96, 165, 250, .12)",
            "& .MuiLinearProgress-bar": { bgcolor: "#60a5fa" },
          }}
        />
      )}

      <DialogContent sx={{ px: 2.4, pt: 0, pb: 2.2, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            mb: 0.55,
          }}
        >
          {["일", "월", "화", "수", "목", "금", "토"].map((weekday) => (
            <Typography
              key={weekday}
              sx={{ py: 0.45, textAlign: "center", color: "#94a3b8", fontSize: 11, fontWeight: 500 }}
            >
              {weekday}
            </Typography>
          ))}
        </Box>
        <Box
          sx={{
            height: "calc(100% - 28px)",
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gridTemplateRows: `repeat(${Math.ceil(monthCells.length / 7)}, minmax(0, 1fr))`,
            gap: 0.55,
          }}
        >
          {monthCells.map((day, index) => {
            const dayEvents = day ? eventsByDay.get(day) || [] : [];
            return (
              <Box
                key={`${day || "blank"}-${index}`}
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  p: day ? 0.7 : 0,
                  borderRadius: "6px",
                  border: day ? "1px solid rgba(148,163,184,.2)" : 0,
                  bgcolor: day ? "rgba(15, 30, 52, .72)" : "transparent",
                  overflow: "hidden",
                }}
              >
                {day && (
                  <>
                    <Typography sx={{ color: "#e2e8f0", fontSize: 11.5, fontWeight: 600 }}>
                      {day}
                    </Typography>
                    <Stack
                      spacing={0.35}
                      sx={{
                        mt: 0.45,
                        pr: dayEvents.length > 3 ? 0.25 : 0,
                        maxHeight: "calc(100% - 20px)",
                        overflowY: "auto",
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(148, 163, 184, .46) transparent",
                        "&::-webkit-scrollbar": { width: 5 },
                        "&::-webkit-scrollbar-track": {
                          bgcolor: "transparent",
                          borderRadius: 5,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: "rgba(148, 163, 184, .38)",
                          borderRadius: 5,
                          border: "1px solid rgba(6, 16, 31, .7)",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          bgcolor: "rgba(147, 197, 253, .68)",
                        },
                      }}
                    >
                      {dayEvents.map((event) => {
                        const EventIcon = event.meta.icon;
                        return (
                          <Box
                            key={event.id}
                            data-testid="dt-safety-calendar-event"
                            role="button"
                            tabIndex={0}
                            onClick={() => onEventOpen(event)}
                            onKeyDown={(keyEvent) => {
                              if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                                keyEvent.preventDefault();
                                onEventOpen(event);
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.45,
                              minWidth: 0,
                              px: 0.65,
                              py: 0.4,
                              borderRadius: "4px",
                              color: "#f8fafc",
                              bgcolor: event.meta.bg,
                              border: `1px solid ${event.meta.color}55`,
                              boxShadow: `inset 3px 0 0 ${event.meta.color}`,
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: event.meta.bg,
                                filter: "brightness(1.2)",
                              },
                            }}
                          >
                            <EventIcon
                              sx={{ color: event.meta.color, fontSize: 13, flexShrink: 0 }}
                            />
                            <Typography
                              noWrap
                              sx={{ color: "#f8fafc", fontSize: 10.5, fontWeight: 600 }}
                            >
                              {event.displayTitle}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function EventDetailDialog({ event, groupCode, projectCode, onClose }) {
  if (!event) return null;
  const actionPath = getEventActionPath(event, groupCode, projectCode);
  const handleOpenSystem = () => {
    onClose();
    window.open(actionPath, "_blank", "noopener,noreferrer");
  };
  const EventIcon = event.meta.icon;
  const isTbmGroup = Boolean(event.isTbmGroup);
  const isTodayTbmSummary = event.type === "tbm_today_summary";
  const isSignalList = event.type === "signal_list";
  const tbmTeamRows = event.childEvents || [];
  const signalRows = isSignalList ? event.childEvents || [] : [];
  const rows = isTbmGroup
    ? []
    : detailRows(event).filter(([, value]) => value !== null && value !== undefined && value !== "");

  return (
    <Dialog
      data-testid="dt-safety-event-detail-dialog"
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...DT_SAFETY_FONT_SCOPE, borderRadius: "8px", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ px: 2.25, py: 1.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.15} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "8px",
                bgcolor: event.meta.bg,
                color: event.meta.color,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <EventIcon sx={{ fontSize: 21 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography className="dt-safety-important" sx={{ color: "#0f172a", fontSize: 17, fontWeight: 700 }}>
                {event.displayTitle}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                {formatKoreanDateTime(event.start, event.allDay)}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} aria-label="상세 닫기">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2.25 }}>
        <Stack spacing={1.1}>
          <Chip
            label={event.meta.label}
            size="small"
            sx={{ alignSelf: "flex-start", bgcolor: event.meta.bg, color: event.meta.color, fontWeight: 650 }}
          />
          {isTbmGroup ? (
            <Box
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.45, py: 1.1, bgcolor: "#fff7ed", borderBottom: "1px solid #fed7aa" }}
              >
                <Typography sx={{ color: "#9a3412", fontSize: 12.5, fontWeight: 700 }}>
                  {isTodayTbmSummary ? "팀별 TBM 현황" : "미실시 팀 목록"}
                </Typography>
                <Chip
                  label={`${tbmTeamRows.length}팀`}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: "#ffedd5",
                    color: "#c2410c",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Stack sx={{ maxHeight: 280, overflowY: "auto" }}>
                {tbmTeamRows.map((teamEvent) => {
                  const raw = teamEvent.raw || {};
                  const statusChip = getTbmStatusChipSx(raw.status);
                  return (
                    <Stack
                      key={teamEvent.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{
                        px: 1.45,
                        py: 1.05,
                        borderTop: "1px solid #f1f5f9",
                        "&:first-of-type": { borderTop: 0 },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ color: "#0f172a", fontSize: 13, fontWeight: 650 }}>
                          {raw.team_name || teamEvent.title || "대상 팀"}
                        </Typography>
                        <Typography sx={{ mt: 0.15, color: "#64748b", fontSize: 11.5 }}>
                          대상 {raw.total_members || 0}명
                        </Typography>
                      </Box>
                      <Chip
                        label={statusChip.label}
                        size="small"
                        sx={{
                          height: 22,
                          bgcolor: statusChip.bgcolor,
                          color: statusChip.color,
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ) : isSignalList ? (
            <Box
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.45, py: 1.1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}
              >
                <Typography sx={{ color: "#334155", fontSize: 12.5, fontWeight: 700 }}>
                  대상 목록
                </Typography>
                <Chip
                  label={`${signalRows.length}건`}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: event.meta.bg,
                    color: event.meta.color,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
              </Stack>
              <Stack sx={{ maxHeight: 280, overflowY: "auto" }}>
                {signalRows.length ? (
                  signalRows.map((signalEvent) => {
                    const source = getEventSource(signalEvent);
                    const meta = EVENT_META[source] || event.meta;
                    const RowIcon = meta.icon || EventIcon;
                    return (
                      <Stack
                        key={signalEvent.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          px: 1.45,
                          py: 1.05,
                          borderTop: "1px solid #f1f5f9",
                          "&:first-of-type": { borderTop: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "7px",
                            bgcolor: meta.bg,
                            color: meta.color,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          <RowIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.6} sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: "#64748b", fontSize: 11.5, flexShrink: 0 }}>
                              {signalEvent.timingLabel}
                            </Typography>
                            <Typography sx={{ color: "#94a3b8", fontSize: 11.5, flexShrink: 0 }}>
                              {formatKoreanDateTime(signalEvent.start, signalEvent.allDay)}
                            </Typography>
                          </Stack>
                          <Typography noWrap sx={{ mt: 0.15, color: "#0f172a", fontSize: 13, fontWeight: 650 }}>
                            {signalEvent.displayTitle}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })
                ) : (
                  <Typography sx={{ px: 1.45, py: 1.3, color: "#64748b", fontSize: 13 }}>
                    표시할 항목이 없습니다.
                  </Typography>
                )}
              </Stack>
            </Box>
          ) : rows.length ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "110px minmax(0, 1fr)",
                columnGap: 1.5,
                rowGap: 0,
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {rows.map(([label, value], index) => (
                <React.Fragment key={label}>
                  <Typography
                    sx={{
                      px: 1.35,
                      py: 1.15,
                      bgcolor: "#f8fafc",
                      color: "#64748b",
                      fontSize: 12,
                      fontWeight: 500,
                      borderTop: index ? "1px solid #e2e8f0" : 0,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      px: 1.35,
                      py: 1.15,
                      color: "#0f172a",
                      fontSize: 12.5,
                      fontWeight: 500,
                      borderTop: index ? "1px solid #e2e8f0" : 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {String(value)}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              등록된 세부 정보가 없습니다.
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            endIcon={<ChevronRightIcon />}
            onClick={handleOpenSystem}
            sx={{
              mt: 0.35,
              minHeight: 42,
              borderRadius: "7px",
              bgcolor: "#2563eb",
              boxShadow: "none",
              fontWeight: 700,
              "&:hover": {
                bgcolor: "#1d4ed8",
                boxShadow: "none",
              },
            }}
          >
            안전관리 시스템에서 보기
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function SafetyOverviewOverlay({ groupCode = "dev", projectCode = "ese" }) {
  const [metrics, setMetrics] = useState({
    pending: 0,
    evaluated: 0,
    improving: 0,
    improved: 0,
    highRisk: 0,
  });
  const [highRiskMachines, setHighRiskMachines] = useState([]);
  const [events, setEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [monthlyPanelCollapsed, setMonthlyPanelCollapsed] = useState(false);
  const [queuePanelCollapsed, setQueuePanelCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const now = useMemo(() => new Date(), []);
  const monthLabel = `${now.getFullYear()}년 ${now.getMonth() + 1}월`;

  const load = useCallback(async () => {
    setLoading(true);
    const calendarStart = addDays(now, -45);
    const calendarEnd = addDays(now, 45);
    const [overviewResult, calendarResult] = await Promise.allSettled([
      getSafetyOverview({ groupCode, projectCode, date: now }),
      getUpcomingSafetyEvents({
        groupCode,
        projectCode,
        start: formatDate(calendarStart),
        end: formatDate(calendarEnd),
      }),
    ]);

    let failed = false;
    if (overviewResult.status === "fulfilled") {
      const data = overviewResult.value;
      const completedEvaluations = data.evaluations.filter(
        (row) => toNumber(row?.eval_status) >= 20
      );
      const latestEvaluations = buildLatestEvaluations(data.evaluations);
      setMetrics({
        pending: uniqueCount(data.pendingHazards, "register_cause_id"),
        evaluated: completedEvaluations.length,
        improving: data.improvementWaiting.filter((row) => !isCompletedImprovement(row)).length,
        improved: data.improvementCompleted.filter(isCompletedImprovement).length,
        highRisk: latestEvaluations.filter(isHighRisk).length,
      });
      setHighRiskMachines(buildHighRiskMachines(data.evaluations));
      failed = data.hasError;
    } else {
      failed = true;
      setHighRiskMachines([]);
    }

    if (calendarResult.status === "fulfilled") {
      setCalendarData(calendarResult.value || {});
      setEvents(normalizeCalendarEvents(calendarResult.value?.events));
    } else {
      failed = true;
      setCalendarData({});
      setEvents([]);
    }

    setHasError(failed);
    setLoading(false);
  }, [groupCode, projectCode, now]);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [load]);

  const loadCalendarMonth = useCallback(
    async (monthDate) => {
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      setCalendarLoading(true);
      try {
        const result = await getUpcomingSafetyEvents({
          groupCode,
          projectCode,
          start: formatDate(monthStart),
          end: formatDate(monthEnd),
        });
        setCalendarEvents(normalizeCalendarEvents(result?.events));
      } catch (error) {
        setCalendarEvents([]);
        setHasError(true);
      } finally {
        setCalendarLoading(false);
      }
    },
    [groupCode, projectCode]
  );

  const priorityEvents = normalizePriorityEvents(events, now).filter(
    (event) => !dismissedIds.has(String(event.id))
  );
  const visibleEvents = normalizeUpcomingEvents(events, now)
    .filter((event) => !dismissedIds.has(String(event.id)))
    .filter((event) => !isPriorityEvent(event, now));
  const signalSummary = createSignalSummary(events, now, calendarData);

  const openCalendar = (event) => {
    const eventDate = new Date(event?.start || now);
    const targetMonth = new Date(eventDate.getFullYear(), eventDate.getMonth(), 1);
    setCalendarMonth(targetMonth);
    setCalendarOpen(true);
    loadCalendarMonth(targetMonth);
  };

  const openQueueEvent = (event, options = {}) => {
    if (options.priority || event?.isTbmGroup) {
      setSelectedEvent(event);
      return;
    }
    openCalendar(event);
  };

  const changeCalendarMonth = (nextMonth) => {
    setCalendarMonth(nextMonth);
    loadCalendarMonth(nextMonth);
  };

  return (
    <>
      <MonthlyStatusPanel
        monthLabel={monthLabel}
        metrics={metrics}
        loading={loading}
        hasError={hasError}
        highRiskMachines={highRiskMachines}
        collapsed={monthlyPanelCollapsed}
        onToggle={() => setMonthlyPanelCollapsed((value) => !value)}
      />

      <ActionQueuePanel
        priorityEvents={priorityEvents}
        futureEvents={visibleEvents}
        signalSummary={signalSummary}
        today={now}
        onOpen={openQueueEvent}
        onOpenCalendar={() => openCalendar()}
        onClose={(id) =>
          setDismissedIds((previous) => {
            const next = new Set(previous);
            next.add(String(id));
            return next;
          })
        }
        collapsed={queuePanelCollapsed}
        onToggle={() => setQueuePanelCollapsed((value) => !value)}
      />

      <SafetyCalendarDialog
        open={calendarOpen}
        monthDate={calendarMonth}
        events={calendarEvents}
        loading={calendarLoading}
        onMonthChange={changeCalendarMonth}
        onEventOpen={setSelectedEvent}
        onClose={() => setCalendarOpen(false)}
      />

      <EventDetailDialog
        event={selectedEvent}
        groupCode={groupCode}
        projectCode={projectCode}
        onClose={() => setSelectedEvent(null)}
      />
    </>
  );
}
